import { Title, Text } from "@mantine/core"
import z from "zod"

import { CommunicationStudioClasses } from "@tfg/types/constants/communication-studio.constant"
import {
  AGECResponses,
  ProductSheetLanguages,
  ProductSheetTranslations,
} from "@tfg/types/endpoints/agec"

import { useGetTranslationsContext } from "../context/translationsContext/useGetTranslationsContext"

export type DetailedProductCompositionProps = {
  agecData: AGECResponses.GenericAgecData
}

const getTranslation = (
  translations: ProductSheetTranslations,
  currentLanguage: string
): string => {
  const { success, data: language } = z
    .enum(ProductSheetLanguages)
    .safeParse(currentLanguage.toUpperCase())

  if (
    !success ||
    !language ||
    !(language in translations) ||
    !translations[language]
  ) {
    return translations.originalText
  }

  return translations[language]
}

export const DetailedProductComposition = ({
  agecData,
}: DetailedProductCompositionProps) => {
  const { t, currentLanguage } = useGetTranslationsContext()

  const composition = agecData.components.map((component) => ({
    key: component.itemId,
    name: getTranslation(component.translations, currentLanguage),
    composition: component.materials
      .map(
        (material) =>
          `${material.proportion}% ${getTranslation(material.translations, currentLanguage)}`
      )
      .join(", "),
  }))

  return (
    <div key={`detailed-product-composition-block`}>
      <Title
        order={3}
        className={
          CommunicationStudioClasses.DETAILED_PRODUCT_COMPOSITION_TITLE
        }
      >
        {t("detailedProductComposition.title")}
      </Title>
      {composition.map((component) => (
        <Text
          key={component.key}
          className={
            CommunicationStudioClasses.DETAILED_PRODUCT_COMPOSITION_CONTENT
          }
        >
          {component.name} : {component.composition}
        </Text>
      ))}
    </div>
  )
}
